import { ComponentProps } from 'react';
import { OnboardingAuditStatus } from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import { createHandler, Handler } from '@mews/std';
import { Tag } from '@optimus-web/core';

interface AuditStatusTagProps {
    status: OnboardingAuditStatus;
}

export const tagVariantHandler = createHandler<OnboardingAuditStatus, ComponentProps<typeof Tag>['variant']>({
    [OnboardingAuditStatus.Advice]: 'warning',
    [OnboardingAuditStatus.Completed]: 'success',
    [OnboardingAuditStatus.Incomplete]: 'basicSubtle',
    [OnboardingAuditStatus.NoGo]: 'danger',
    [OnboardingAuditStatus.NotApplicable]: 'basicSubtle',
});

type StatusHandler = Handler<OnboardingAuditStatus, JSX.Element>;

const createTagFromHandler =
    (handler: StatusHandler) =>
    ({ status }: AuditStatusTagProps) =>
        handler(status);

const OverallHandler: StatusHandler = createHandler({
    [OnboardingAuditStatus.Advice]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.Advice)}>{lt('GoLiveAuditsStatusOverviewAdvice')}</Tag>
    ),
    [OnboardingAuditStatus.Completed]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.Completed)}>
            {lt('GoLiveAuditsStatusOverviewCompleted')}
        </Tag>
    ),
    [OnboardingAuditStatus.Incomplete]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.Incomplete)}>
            {lt('GoLiveAuditsStatusOverviewIncomplete')}
        </Tag>
    ),
    [OnboardingAuditStatus.NoGo]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.NoGo)}>{lt('GoLiveAuditsStatusOverviewNoGo')}</Tag>
    ),
    [OnboardingAuditStatus.NotApplicable]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.NotApplicable)}>
            {lt('GoLiveAuditsStatusOverviewNotApplicable')}
        </Tag>
    ),
});

/** Simple handler for us for both the Section and Task level */
const AuditStatusHandler: StatusHandler = createHandler({
    [OnboardingAuditStatus.Advice]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.Advice)}>{lt('GoLiveAuditsStatusTaskAdvice')}</Tag>
    ),
    [OnboardingAuditStatus.Completed]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.Completed)}>{lt('GoLiveAuditsStatusTaskCompleted')}</Tag>
    ),
    [OnboardingAuditStatus.Incomplete]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.Incomplete)}>
            {lt('GoLiveAuditsStatusTaskIncomplete')}
        </Tag>
    ),
    [OnboardingAuditStatus.NoGo]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.NoGo)}>{lt('GoLiveAuditsStatusTaskNoGo')}</Tag>
    ),
    [OnboardingAuditStatus.NotApplicable]: (
        <Tag variant={tagVariantHandler(OnboardingAuditStatus.NotApplicable)}>
            {lt('GoLiveAuditsStatusOverviewNotApplicable')}
        </Tag>
    ),
});

export const OverallStatusTag = createTagFromHandler(OverallHandler);
export const SectionStatusTag = createTagFromHandler(AuditStatusHandler);
export const TaskStatusTag = createTagFromHandler(AuditStatusHandler);
